import { useEffect } from 'react';
import { Link, isRouteErrorResponse, useLocation, useRouteError } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Button } from '@localagent/ui/components/button';
import { addErrorEntryAction } from '../stores/errors/atoms';

const ROUTE_ERROR_CODE = 'ROUTE_RENDER_FAILED';
const ROUTE_ERROR_ORIGIN = 'AppShell';

interface DescribedRouteError {
  title: string;
  message: string;
}

function describeRouteError(error: unknown): DescribedRouteError {
  if (isRouteErrorResponse(error)) {
    const body =
      typeof error.data === 'string' ? error.data : JSON.stringify(error.data ?? null);
    return { title: `${error.status} ${error.statusText}`, message: body };
  }
  if (error instanceof Error) {
    return { title: error.name, message: error.message };
  }
  return { title: 'Route error', message: String(error) };
}

let routeErrorCounter = 0;

export function RouteErrorView({ chromeless = false }: { chromeless?: boolean }) {
  const error = useRouteError();
  const location = useLocation();
  const addError = useSetAtom(addErrorEntryAction);
  const { title, message } = describeRouteError(error);
  const path = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    console.error('Route render failed at', path, error);
    routeErrorCounter++;
    const now = new Date();
    addError({
      id: `route-error-${now.getTime()}-${routeErrorCounter}`,
      eventIds: [`route-error:${path}:${now.getTime()}:${routeErrorCounter}`],
      error_code: ROUTE_ERROR_CODE,
      message: `${title}: ${message}`,
      service_origin: ROUTE_ERROR_ORIGIN,
      severity: 'error',
      details: { path },
      timestamp: now.toISOString(),
      occurrenceCount: 1,
      read: false,
    });
  }, [addError, error, path, title, message]);

  const container = chromeless
    ? 'flex min-h-screen w-full items-center justify-center bg-background p-8'
    : 'flex h-full w-full items-center justify-center p-8';

  return (
    <div className={container} data-testid="route-error-view">
      <div className="max-w-lg text-center">
        <h1 className="text-title-1 font-semibold text-destructive" data-testid="route-error-title">
          {title}
        </h1>
        <p className="mt-4 text-body text-muted-foreground" data-testid="route-error-message">
          {message}
        </p>
        <p className="mt-2 text-caption text-muted-foreground">
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono" data-testid="route-error-path">
            {path}
          </code>
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button onClick={() => window.location.reload()} data-testid="route-error-reload">
            Reload
          </Button>
          <Link
            to="/chat"
            className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2 text-label font-medium text-secondary-foreground hover:bg-secondary/80"
            data-testid="route-error-chat-link"
          >
            Go to Chat
          </Link>
        </div>
      </div>
    </div>
  );
}
